import React, { useState, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const IANA_TIMEZONES: string[] =
  (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf('timeZone');

interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const TimezoneSelect: React.FC<TimezoneSelectProps> = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const label = value || 'Select timezone...';

  const filtered = useMemo(() => {
    if (!search) return IANA_TIMEZONES;
    const lower = search.toLowerCase();
    return IANA_TIMEZONES.filter((tz) => tz.toLowerCase().includes(lower));
  }, [search]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls="timezone-listbox"
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-prosigliere-grey bg-pure-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-prosigliere-blue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            !value && 'text-almost-black/40'
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-[var(--radix-popover-trigger-width)] rounded-md border border-prosigliere-grey bg-pure-white shadow-md"
          sideOffset={4}
          align="start"
        >
          <Command shouldFilter={false}>
            <Command.Input
              placeholder="Search timezone..."
              value={search}
              onValueChange={setSearch}
              className="h-10 w-full border-b border-prosigliere-grey bg-transparent px-3 py-2 text-sm outline-none placeholder:text-almost-black/40"
            />
            <Command.List id="timezone-listbox" className="max-h-60 overflow-y-auto p-1">
              <Command.Empty className="py-6 text-center text-sm text-almost-black/60">
                No timezone found.
              </Command.Empty>
              {filtered.map((tz) => (
                <Command.Item
                  key={tz}
                  value={tz}
                  onSelect={() => {
                    onChange(tz);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-prosigliere-grey/20 data-[selected]:bg-prosigliere-grey/20"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {value === tz && <Check className="h-4 w-4" />}
                  </span>
                  {tz}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export { TimezoneSelect };
